import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, allComics } from '@/mocks/comics';
import SchemaInjector from '@/components/base/SchemaInjector';
import { getWebPageSchema, getBreadcrumbSchema } from '@/mocks/schema';

type FilterType = 'all' | 'hot' | 'new';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug) || categories[0];
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  const categoryComics = allComics.filter((c) => {
    const matchesCategory = c.categorySlug === (slug || 'romance');
    if (!matchesCategory) return false;
    if (activeFilter === 'hot') return c.isHot;
    if (activeFilter === 'new') return c.isNew;
    return true;
  });

  const schemas = [
    getWebPageSchema({
      title: `${category.name} - 韩漫之家 | 韩国漫画免费阅读`,
      description: `韩漫之家${category.name}分类，收录${category.count}部优质韩国漫画，持续更新中。`,
      url: `http://informyi.com/category/${slug}`,
    }),
    getBreadcrumbSchema([
      { name: '首页', url: 'http://informyi.com/' },
      { name: category.name, url: `http://informyi.com/category/${slug}` },
    ]),
  ];

  return (
    <>
      <SchemaInjector schema={schemas} />
      <Navbar />
      <main>
        <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20of%20a%20cozy%20reading%20nook%20with%20shelves%20of%20colorful%20comic%20books%2C%20warm%20ambient%20lighting%2C%20soft%20pastel%20color%20palette%2C%20dreamy%20atmosphere%2C%20artistic%20composition%2C%20gentle%20bokeh%20background&width=1600&height=500&seq=category-hero-01&orientation=landscape"
            alt={`${category.name}分类`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-black/40"></div>
          <div className="absolute inset-0 z-10 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row items-center gap-8">
                  <div className="lg:w-2/5 text-center lg:text-left">
                    <div className="w-16 h-16 mx-auto lg:mx-0 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-700 mb-4">
                      <i className={`${category.icon} text-2xl`}></i>
                    </div>
                    <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-3">
                      {category.name}
                    </h1>
                    <p className="text-base text-background-50/80 leading-relaxed max-w-md mx-auto lg:mx-0">
                      共收录 {category.count} 部{category.name}类韩国漫画，持续更新中
                    </p>
                  </div>
                  <div className="hidden lg:block lg:w-3/5"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <nav className="bg-background-50 border-b border-background-200/70">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto py-3">
              <ol className="flex items-center gap-2 text-xs text-foreground-500">
                <li><Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-foreground-950 font-medium">{category.name}</li>
              </ol>
            </div>
          </div>
        </nav>

        <section className="py-8 md:py-10 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                <div className="inline-flex bg-background-50 rounded-full p-1">
                  {([
                    { key: 'all', label: '全部' },
                    { key: 'hot', label: '热门' },
                    { key: 'new', label: '最新' },
                  ] as const).map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveFilter(tab.key)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                        activeFilter === tab.key
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:text-foreground-950'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-foreground-500">
                  共 {categoryComics.length} 部作品
                </p>
              </div>

              {categoryComics.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {categoryComics.map((comic) => (
                    <div key={comic.id} className="group">
                      <Link to={`/article/${comic.slug}`} className="block">
                        <div className="relative rounded-2xl overflow-hidden mb-4" style={{ aspectRatio: '3/4' }}>
                          <img
                            src={comic.imageUrl}
                            alt={comic.title}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                            {comic.isHot && (
                              <span className="bg-accent-500 text-background-50 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
                                HOT
                              </span>
                            )}
                            {comic.isNew && (
                              <span className="bg-primary-500 text-background-50 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
                                NEW
                              </span>
                            )}
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                            <div className="flex items-center gap-2 text-xs text-background-50/80">
                              <span className="flex items-center gap-1">
                                <i className="ri-star-fill text-accent-500"></i> {comic.rating}
                              </span>
                              <span>{comic.views}</span>
                            </div>
                          </div>
                        </div>
                        <span className="inline-block bg-accent-100 text-accent-900 text-xs font-medium px-2.5 py-1 rounded-full mb-2 whitespace-nowrap">
                          {comic.category}
                        </span>
                        <h3 className="text-lg md:text-xl font-bold text-foreground-950 mb-1.5 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {comic.title}
                        </h3>
                        <p className="text-sm text-foreground-600 line-clamp-2 mb-3">{comic.description}</p>
                        <div className="flex items-center gap-3 text-xs text-foreground-500">
                          <span>{comic.author}</span>
                          <span>{comic.chapters}话</span>
                          <span>{comic.views}</span>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200/70 mb-4">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无{activeFilter === 'hot' ? '热门' : activeFilter === 'new' ? '最新' : ''}漫画</p>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-8">
                探索更多分类
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                {categories
                  .filter((c) => c.slug !== slug)
                  .map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 bg-background-100 hover:bg-background-200/70 rounded-xl px-4 py-4 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer group"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors">
                        <i className={`${cat.icon} text-lg`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{cat.name}</p>
                        <p className="text-xs text-foreground-500">{cat.count}部</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}